import type { OutgoingEvent, SdkConfig } from './types';
import type { Consent } from './consent';
import type { Identity } from './identity';
import type { Queue } from './queue';
import type { FormOptinSource } from './form-scan';
import { readCookie, writeCookie, deleteCookie, inferCookieDomain } from './storage';
import { hashCode, nowMs, uuid } from './utils';
import { redactUrl } from './redact';
import { log } from './log';

/**
 * Emits `optin` — "we just learned who this visitor is".
 *
 * Every place the SDK can pick up an identifier funnels through here: a login or
 * register form, a newsletter box, any other form carrying an email, the GA4/GTM
 * dataLayer, `emblue('identify')`, `emblue('setEmail')`, and the explicit
 * `emblue('optin', source)` escape hatch for sites whose capture isn't a `<form>`.
 *
 * Two invariants:
 *
 *  1. **No optin without an identifier.** A password-only login we can't resolve
 *     to an email or an external user id produces nothing — the event exists to
 *     carry identity, and one with neither is a row the lake can't join.
 *  2. **One optin per (identifier, source) per 60 days.** Somebody who logs in
 *     every morning is one opt-in, not thirty. Recurring activity is what
 *     `session.start` and `form_submit` are for. A new email or a new source is
 *     a new opt-in and does fire.
 *
 * The dedup ledger is a cookie of djb2 fingerprints, so it spans page loads and
 * subdomains the same way the identity cookies do. Without cookie consent it
 * degrades to in-memory (per page load) — same tradeoff `anon_id` already makes,
 * and the Queue is holding those events pending consent anyway.
 */

const COOKIE_OPTIN = '_emb_optin';
const SIXTY_DAYS = 60 * 24 * 60 * 60;

/** Ledger cap. Fingerprints are ~7 bytes, so this stays far below any cookie
 *  size limit while covering more sources than a real site has. */
const MAX_LEDGER = 12;

export type OptinSource = FormOptinSource | 'identify' | 'api' | 'datalayer' | (string & {});

export class Optin {
	private ledger: string[] = [];
	private cookieDomain: string | undefined;

	constructor(
		private config: SdkConfig,
		private identity: Identity,
		private consent: Consent,
		private queue: Queue,
	) {
		this.cookieDomain = config.cookieDomain || inferCookieDomain();
	}

	init(): void {
		const stored = readCookie(COOKIE_OPTIN);
		if (!stored) return;
		this.ledger = stored.split(',').filter(Boolean).slice(-MAX_LEDGER);
	}

	/**
	 * Records an opt-in. Returns true when an event was emitted, false when it was
	 * deduped or there was nothing to identify the visitor by.
	 */
	record(source: OptinSource, context?: Record<string, unknown>): boolean {
		if (!this.config.autoTrack.optin) return false;
		if (!source || typeof source !== 'string') return false;

		const emailSha256 = this.identity.emailSha256;
		const externalUserId = this.identity.externalUserId;
		if (!emailSha256 && !externalUserId) {
			log('optin: no identifier yet, skipping', source);
			return false;
		}

		// Fingerprint over the email hash *or* the external id — whichever we have,
		// email first so a site that later starts calling identify() doesn't re-fire
		// an opt-in for a contact it already sent.
		const identifier = emailSha256 || externalUserId;
		const fingerprint = hashCode(`${identifier}|${source}`);
		if (this.ledger.indexOf(fingerprint) !== -1) {
			log('optin: already recorded', source);
			return false;
		}

		this.remember(fingerprint);

		const event: OutgoingEvent = {
			event_id: uuid(),
			name: 'optin',
			timestamp: nowMs(),
			properties: {
				source,
				...(context || {}),
				// Repeated from the batch envelope on purpose: email hashing is async,
				// so the envelope carrying this event may not have caught up yet. The
				// lake needs the opt-in row to be self-contained.
				email_sha256: emailSha256 || undefined,
				external_user_id: externalUserId || undefined,
				identified_by: emailSha256 ? (externalUserId ? 'both' : 'email') : 'external_user_id',
				url: typeof location !== 'undefined' ? redactUrl(location.href) : undefined,
				referrer: typeof document !== 'undefined' && document.referrer ? redactUrl(document.referrer) : undefined,
			},
		};

		this.queue.enqueue(event);
		log('optin: recorded', source);
		return true;
	}

	private remember(fingerprint: string): void {
		this.ledger.push(fingerprint);
		if (this.ledger.length > MAX_LEDGER) this.ledger = this.ledger.slice(-MAX_LEDGER);
		if (!this.consent.canSetCookies()) return;
		writeCookie(COOKIE_OPTIN, this.ledger.join(','), {
			maxAgeSeconds: SIXTY_DAYS,
			domain: this.cookieDomain,
		});
	}

	/** Called from `reset()` / `eraseMe()`: forgetting the visitor has to forget
	 *  that we opted them in, or the next capture would be silently deduped
	 *  against a contact that no longer exists locally. */
	reset(): void {
		this.ledger = [];
		deleteCookie(COOKIE_OPTIN, { domain: this.cookieDomain });
	}
}
